// @flow

/** @jsx h */

import { bem } from '@nrk/dh-utils'
import { Video } from '@nrk/dh-feature-components'
import { h } from 'preact'
import PureComponent from 'preact-pure-component'
import style from './index.css'

import type { Caption, VideoProps, Viewport } from './types'

type Props = {
  captions?: Caption[],
  horizontal: VideoProps,
  vertical: VideoProps,
  playing: boolean,
  preload?: string,
  muted: boolean,
  viewport: Viewport,
  onCaptionCueEnter: Function,
  onCaptionCueExit: Function,
  onDurationChange: Function,
  onPlay: Function,
  onPause: Function,
  onEnded: Function,
  onTimeUpdate: Function
}

type State = {
  currentTime: number
}

class ResponsiveVideo extends PureComponent<Props, State> {
  state = {
    currentTime: 0
  }

  verticalVideo: Video
  horizontalVideo: Video

  componentDidUpdate (prevProps: Props, prevState: State) {
    const { viewport } = this.props
    const isVerticalViewport = viewport.width / viewport.height < 1
    const prevIsVerticalViewport =
      prevProps.viewport.width / prevProps.viewport.height < 1

    // Update time when switching between vertical/horizontal videos
    if (prevIsVerticalViewport !== isVerticalViewport) {
      this.updateTime(this.state.currentTime)
    }
  }

  play () {
    const { viewport } = this.props
    const isVerticalViewport = viewport.width / viewport.height < 1

    if (isVerticalViewport) {
      this.verticalVideo.play()
    } else {
      this.horizontalVideo.play()
    }
  }

  updateTime (time: number) {
    this.verticalVideo.updateTime(time)
    this.horizontalVideo.updateTime(time)
  }

  handleTimeUpdate = (event: any) => {
    this.props.onTimeUpdate(event)
    this.setState({ currentTime: event.target.currentTime })
  }

  render () {
    const {
      muted,
      captions,
      preload,
      playing,
      horizontal,
      vertical,
      viewport,
      onCaptionCueEnter,
      onCaptionCueExit,
      onDurationChange,
      onPlay,
      onPause,
      onEnded
    } = this.props
    const isVerticalViewport = viewport.width / viewport.height < 1
    const className = bem(
      style.videoPlayer__video,
      isVerticalViewport ? 'vertical' : 'horizontal'
    )

    const commonProps = {
      muted,
      captions,
      preload,
      onCaptionCueEnter,
      onCaptionCueExit,
      onDurationChange,
      onPlay,
      onPause,
      onEnded,
      onTimeUpdate: this.handleTimeUpdate
    }

    return (
      <div className={className}>
        <Video
          {...commonProps}
          ref={verticalVideo => (this.verticalVideo = verticalVideo)}
          className={style.videoPlayer__verticalVideo}
          poster={vertical.poster}
          playing={isVerticalViewport && playing}
          sources={vertical.sources}
        />
        <Video
          {...commonProps}
          ref={horizontalVideo => (this.horizontalVideo = horizontalVideo)}
          className={style.videoPlayer__horizontalVideo}
          poster={horizontal.poster}
          playing={!isVerticalViewport && playing}
          sources={horizontal.sources}
        />
      </div>
    )
  }
}

export default ResponsiveVideo
